import { AlertTriangle, XCircle } from 'lucide-react'

import { cn } from '@/ui/utils/style'

import { EulerValidationResult } from '../types'

interface ValidationAlertProps {
  validation: EulerValidationResult
  className?: string
}

export function ValidationAlert({ validation, className = '' }: ValidationAlertProps) {
  if (validation.severity === 'info' || !validation.message) {
    return null
  }

  const bgColor =
    validation.severity === 'error' ? 'bg-red-500/10 border-red-500/20' : 'bg-yellow-500/10 border-yellow-500/20'

  const textColor = validation.severity === 'error' ? 'text-red-400' : 'text-yellow-400'

  const Icon = validation.severity === 'error' ? XCircle : AlertTriangle

  return (
    <div className={cn('flex items-start gap-3 rounded-xl border p-4', bgColor, className)}>
      <Icon className={cn('h-5 w-5 shrink-0', textColor)} />
      <p className={cn('text-sm', textColor)}>{validation.message}</p>
    </div>
  )
}

/**
 * Simple status indicator for supply/borrow status
 */
interface StatusIndicatorProps {
  status: string
  showLabel?: boolean
}

export function StatusIndicator({ status, showLabel = false }: StatusIndicatorProps) {
  const getStatusColor = () => {
    switch (status) {
      case 'cap-reached':
      case 'no-liquidity':
      case 'paused':
        return 'bg-red-500'
      case 'cap-warning':
      case 'low-liquidity':
        return 'bg-yellow-500'
      default:
        return 'bg-green-500'
    }
  }

  const getStatusLabel = () => {
    switch (status) {
      case 'cap-reached':
        return 'Cap Reached'
      case 'cap-warning':
        return 'Near Cap'
      case 'no-liquidity':
        return 'No Liquidity'
      case 'low-liquidity':
        return 'Low Liquidity'
      case 'paused':
        return 'Paused'
      default:
        return 'Available'
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div className={`h-2 w-2 rounded-full ${getStatusColor()}`} />
      {showLabel && <span className="text-muted-foreground text-xs">{getStatusLabel()}</span>}
    </div>
  )
}
